import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdminProviderConfigResult } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";
import { TabbedPage } from "../components/TabbedPage";

interface FormState {
  name: string;
  logoUrl: string;
  termsUrl: string;
  disclosureText: string;
  disclosureUrl: string;
  disclosureVersion: string;
  termsTitle: string;
  termsText: string;
  termsPageUrl: string;
  termsRequired: boolean;
  privacyUrl: string;
  supportUrl: string;
  contentPolicyUrl: string;
  adminAccessTrackingEnabled: boolean;
  adminGeoEnabled: boolean;
}

const EMPTY_FORM: FormState = {
  name: "",
  logoUrl: "",
  termsUrl: "",
  disclosureText: "",
  disclosureUrl: "",
  disclosureVersion: "1",
  termsTitle: "",
  termsText: "",
  termsPageUrl: "",
  termsRequired: true,
  privacyUrl: "",
  supportUrl: "",
  contentPolicyUrl: "",
  adminAccessTrackingEnabled: true,
  adminGeoEnabled: true,
};

function formFromResult(result: AdminProviderConfigResult): FormState {
  return {
    name: result.config?.name ?? "",
    logoUrl: result.config?.logoUrl ?? "",
    termsUrl: result.config?.termsUrl ?? "",
    disclosureText: result.config?.trackingDisclosureText ?? "",
    disclosureUrl: result.config?.trackingDisclosureUrl ?? "",
    disclosureVersion: String(result.config?.trackingDisclosureVersion ?? 1),
    termsTitle: result.config?.providerTermsTitle ?? "",
    termsText: result.config?.providerTermsText ?? "",
    termsPageUrl: result.config?.providerTermsUrl ?? "",
    termsRequired: result.config?.providerTermsRequired !== false,
    privacyUrl: result.config?.privacyUrl ?? "",
    supportUrl: result.config?.supportUrl ?? "",
    contentPolicyUrl: result.config?.contentPolicyUrl ?? "",
    adminAccessTrackingEnabled: result.config?.adminAccessTrackingEnabled !== false,
    adminGeoEnabled: result.config?.adminGeoEnabled !== false,
  };
}

/**
 * Provider Settings tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v1) —
 * mirrors `apps/admin-web/src/pages/ProviderSettingsPage.tsx` (itself mirroring
 * `apps/admin/app/(tabs)/provider-settings.tsx` / `specs/admin/screens/provider-settings.md`): the
 * DB-backed provider identity + tracking-disclosure (docs/300) + provider terms of usage + legal
 * links editor (docs/312), plus a per-version acceptance summary. Every `useQuery`/`useMutation`
 * here is lifted UNCHANGED — only the returned JSX (MUI `TextField`/`Checkbox` instead of CoreUI
 * `CFormInput`/`CFormCheck`) differs.
 *
 * **Reorganized onto `TabbedPage` (docs/365-ADMIN-USER-COMPLETION-AND-CATALOGS-TABS.md item 3):**
 * the four cards are grouped into three tabs — "Identity" (provider identity + tracking
 * disclosure), "Terms of usage" (the terms editor + its acceptance history), "Legal links" — a
 * pure presentation regroup: the form/mutation stay ONE shared `FormState`/`saveMutation`, so
 * every Save button still submits the WHOLE form regardless of which tab is active. The error
 * alert + "Save all" button + "Saved." status stay OUTSIDE the tabs (apply globally, always
 * visible).
 */
export function ProviderSettingsPage() {
  const { client, token } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [termsVersion, setTermsVersion] = useState(0);
  const [defaults, setDefaults] = useState<AdminProviderConfigResult["defaults"] | null>(null);
  const [saved, setSaved] = useState(false);

  const configQuery = useQuery({
    queryKey: ["admin-provider-config"],
    queryFn: () => client!.adminProviderConfig(token!),
    enabled: !!client && !!token,
  });
  const acceptancesQuery = useQuery({
    queryKey: ["admin-terms-acceptances"],
    queryFn: () => client!.adminTermsAcceptances(token!),
    enabled: !!client && !!token,
  });

  useEffect(() => {
    if (!configQuery.data) return;
    setForm(formFromResult(configQuery.data));
    setTermsVersion(configQuery.data.config?.providerTermsVersion ?? 0);
    setDefaults(configQuery.data.defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configQuery.data]);

  const acceptanceByVersion = useMemo(() => {
    const counts = new Map<number, number>();
    for (const a of acceptancesQuery.data ?? []) counts.set(a.version, (counts.get(a.version) ?? 0) + 1);
    return [...counts.entries()].map(([version, count]) => ({ version, count })).sort((a, b) => a.version - b.version);
  }, [acceptancesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (bumpTermsVersion: boolean) => {
      const parsedVersion = Number(form.disclosureVersion);
      return client!.adminSetProviderConfig(token!, {
        name: form.name.trim() ? form.name.trim() : null,
        logoUrl: form.logoUrl.trim() ? form.logoUrl.trim() : null,
        termsUrl: form.termsUrl.trim() ? form.termsUrl.trim() : null,
        trackingDisclosureText: form.disclosureText.trim() ? form.disclosureText.trim() : null,
        trackingDisclosureUrl: form.disclosureUrl.trim() ? form.disclosureUrl.trim() : null,
        ...(Number.isFinite(parsedVersion) && parsedVersion > 0 ? { trackingDisclosureVersion: Math.floor(parsedVersion) } : {}),
        providerTermsTitle: form.termsTitle.trim() ? form.termsTitle.trim() : null,
        providerTermsText: form.termsText.trim() ? form.termsText.trim() : null,
        providerTermsUrl: form.termsPageUrl.trim() ? form.termsPageUrl.trim() : null,
        providerTermsRequired: form.termsRequired,
        bumpTermsVersion,
        privacyUrl: form.privacyUrl.trim() ? form.privacyUrl.trim() : null,
        supportUrl: form.supportUrl.trim() ? form.supportUrl.trim() : null,
        contentPolicyUrl: form.contentPolicyUrl.trim() ? form.contentPolicyUrl.trim() : null,
        adminAccessTrackingEnabled: form.adminAccessTrackingEnabled,
        adminGeoEnabled: form.adminGeoEnabled,
      });
    },
    onSuccess: (config) => {
      setTermsVersion(config.providerTermsVersion ?? 0);
      setSaved(true);
      void acceptancesQuery.refetch();
    },
  });

  const defaultHint = (value: string | null | undefined) => (value ? `Currently defaults to: ${value}` : null);

  if (configQuery.isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (configQuery.isError) {
    return <Alert severity="error">{(configQuery.error as Error).message}</Alert>;
  }

  return (
    <>
      <TabbedPage
        defaultTab="identity"
        tabs={[
          {
            id: "identity",
            label: "Identity",
            content: (
              <>
                <MainCard title="Provider identity" sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    A blank field falls back to this deployment's env-var default.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      size="small"
                      label="Provider name"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      helperText={!form.name.trim() ? defaultHint(defaults?.name) : undefined}
                    />
                    <TextField
                      size="small"
                      label="Logo URL"
                      value={form.logoUrl}
                      onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
                      helperText={!form.logoUrl.trim() ? defaultHint(defaults?.logoUrl) : undefined}
                    />
                    <TextField
                      size="small"
                      label="Terms URL (plain link)"
                      value={form.termsUrl}
                      onChange={(e) => setForm((f) => ({ ...f, termsUrl: e.target.value }))}
                      helperText={!form.termsUrl.trim() ? defaultHint(defaults?.termsUrl) : undefined}
                    />
                  </Stack>
                </MainCard>

                <MainCard title="Tracking disclosure">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Shown as a required checkbox on the hosted sign-up page. Device/usage recording is
                    gated on acceptance of this version.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      multiline
                      minRows={3}
                      label="Disclosure text"
                      value={form.disclosureText}
                      onChange={(e) => setForm((f) => ({ ...f, disclosureText: e.target.value }))}
                    />
                    <TextField
                      size="small"
                      label="Disclosure URL (optional)"
                      value={form.disclosureUrl}
                      onChange={(e) => setForm((f) => ({ ...f, disclosureUrl: e.target.value }))}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Version"
                      value={form.disclosureVersion}
                      onChange={(e) => setForm((f) => ({ ...f, disclosureVersion: e.target.value }))}
                      sx={{ maxWidth: 200 }}
                    />
                  </Stack>
                </MainCard>

                <MainCard title="Admin analytics tracking">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Controls the admin{" "}
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      Analytics
                    </Typography>{" "}
                    page's page-access beacon (which admin route was opened, and when) — a
                    separate, staff-only signal from the tracking disclosure above, which
                    governs END-USER device data. Off means the beacon still fires but records
                    nothing.
                  </Typography>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.adminAccessTrackingEnabled}
                          onChange={(e) => setForm((f) => ({ ...f, adminAccessTrackingEnabled: e.target.checked }))}
                        />
                      }
                      label="Track admin page views"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.adminGeoEnabled}
                          disabled={!form.adminAccessTrackingEnabled}
                          onChange={(e) => setForm((f) => ({ ...f, adminGeoEnabled: e.target.checked }))}
                        />
                      }
                      label="Attach country (from Cloudflare) to tracked views"
                    />
                  </Stack>
                </MainCard>
              </>
            ),
          },
          {
            id: "terms",
            label: "Terms of usage",
            content: (
              <>
                <MainCard title="Provider terms of usage" sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    A separate, versioned acceptance-gated terms declaration for THIS provider's
                    content.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Not the same as the tracking disclosure above — accepting these terms is not
                    consent to device/usage sharing.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      size="small"
                      label="Title"
                      value={form.termsTitle}
                      onChange={(e) => setForm((f) => ({ ...f, termsTitle: e.target.value }))}
                    />
                    <TextField
                      multiline
                      minRows={4}
                      label="Terms text"
                      value={form.termsText}
                      onChange={(e) => setForm((f) => ({ ...f, termsText: e.target.value }))}
                    />
                    <TextField
                      size="small"
                      label="Terms page URL"
                      value={form.termsPageUrl}
                      onChange={(e) => setForm((f) => ({ ...f, termsPageUrl: e.target.value }))}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.termsRequired}
                          onChange={(e) => setForm((f) => ({ ...f, termsRequired: e.target.checked }))}
                        />
                      }
                      label="Required to accept"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {termsVersion > 0 ? `Current version: v${termsVersion}` : "Not configured yet — saving will start at v1."}
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      <Button variant="contained" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate(false)}>
                        Save
                      </Button>
                      <Button variant="outlined" color="secondary" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate(true)}>
                        Save &amp; require re-acceptance
                      </Button>
                    </Stack>
                  </Stack>
                </MainCard>

                <MainCard title="Provider terms acceptance">
                  {acceptanceByVersion.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 0 }}>
                      No acceptances recorded yet.
                    </Typography>
                  ) : (
                    <List dense>
                      {acceptanceByVersion.map((row) => (
                        <ListItem key={row.version} disableGutters>
                          <ListItemText primary={`v${row.version}`} />
                          <Typography color="text.secondary">{row.count}</Typography>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </MainCard>
              </>
            ),
          },
          {
            id: "legal",
            label: "Legal links",
            content: (
              <MainCard title="Legal links">
                <Stack spacing={2}>
                  <TextField
                    size="small"
                    label="Privacy policy URL"
                    value={form.privacyUrl}
                    onChange={(e) => setForm((f) => ({ ...f, privacyUrl: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="Support URL"
                    value={form.supportUrl}
                    onChange={(e) => setForm((f) => ({ ...f, supportUrl: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="Content policy URL"
                    value={form.contentPolicyUrl}
                    onChange={(e) => setForm((f) => ({ ...f, contentPolicyUrl: e.target.value }))}
                  />
                </Stack>
              </MainCard>
            ),
          },
        ]}
      />

      {saveMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(saveMutation.error as Error).message}
        </Alert>
      ) : null}
      <Button variant="contained" sx={{ mb: 2 }} disabled={saveMutation.isPending} onClick={() => saveMutation.mutate(false)}>
        {saveMutation.isPending ? <CircularProgress size={18} color="inherit" /> : "Save all"}
      </Button>
      {saved ? (
        <Typography variant="body2" color="text.secondary">
          Saved.
        </Typography>
      ) : null}
    </>
  );
}
